const RESOURCES = 'product,collection,query,page,article';
const DEBOUNCE = 200;
const MIN_CHARS = 2;

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    if (!this.input || !this.results) return;

    this.limit = Number(this.dataset.limit) || 4;
    this.timer = null;
    this.controller = null;

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('focus', () => {
      if (this.results.innerHTML.trim()) this.open();
    });
    this.addEventListener('keydown', (event) => this.onKeydown(event));

    this.onDocClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    document.addEventListener('click', this.onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
  }

  onInput() {
    const term = this.input.value.trim();
    clearTimeout(this.timer);

    if (term.length < MIN_CHARS) {
      this.controller?.abort();
      this.clear();
      return;
    }

    this.timer = setTimeout(() => this.fetch(term), DEBOUNCE);
  }

  async fetch(term) {
    // A new keystroke invalidates the request in flight; without this, a slow early
    // response can land after a fast later one and show stale results.
    this.controller?.abort();
    this.controller = new AbortController();

    const params = new URLSearchParams({
      q: term,
      'resources[type]': RESOURCES,
      'resources[limit]': String(this.limit),
      // Without limit_scope=each the limit is the total across all types, which yields
      // one product and three near-useless single rows.
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });

    this.setAttribute('loading', '');
    try {
      const response = await fetch(`${window.Shopify.routes.root}search/suggest?${params}`, {
        signal: this.controller.signal,
      });
      if (!response.ok) throw new Error(response.statusText);
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const section = parsed.querySelector('#predictive-search-results');
      this.results.innerHTML = section ? parsed.body.innerHTML : '';
      section ? this.open() : this.close();
    } catch (error) {
      if (error.name !== 'AbortError') this.clear();
    } finally {
      this.removeAttribute('loading');
    }
  }

  get options() {
    return Array.from(this.results.querySelectorAll('[role="option"]'));
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.clear();
      this.input.focus();
      return;
    }
    if (!this.hasAttribute('open')) return;

    const options = this.options;
    if (!options.length) return;

    const current = options.findIndex((el) => el.getAttribute('aria-selected') === 'true');

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next =
        event.key === 'ArrowDown'
          ? (current + 1) % options.length
          : (current <= 0 ? options.length : current) - 1;
      options.forEach((el, i) => el.setAttribute('aria-selected', String(i === next)));
      const link = options[next].querySelector('a');
      if (link) this.input.setAttribute('aria-activedescendant', (link.id ||= `ps-option-${next}`));
      options[next].scrollIntoView({ block: 'nearest' });
      return;
    }

    if (event.key === 'Enter' && current > -1) {
      const link = options[current].querySelector('a');
      if (link) {
        event.preventDefault();
        link.click();
      }
    }
  }

  open() {
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  clear() {
    this.results.innerHTML = '';
    this.close();
  }
}

customElements.define('predictive-search', PredictiveSearch);
